/* eslint-disable no-debugger */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { useImporter } from "../../contexts/importer-context";
import { usePreviewContext } from "../../contexts/preview-context";
import { usePhotoPreprocessProgress } from "../../hooks/use-photo-preprocess-progress";
import { useAppContext } from "../../hooks/useAppContext";
import { useEditor } from "../../hooks/useEditor";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useFeatureFlagToggle } from "../../hooks/useFeatureFlags";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useIsTextEditMode } from "../../hooks/useIsTextEditMode";
import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { useLabels } from "../../hooks/useLabels";
import { useMessage } from "../../hooks/useMessage";
import { usePreviewBox } from "../../hooks/usePreviewBox";
import {
	useLogicalSelectedPage,
	useSelectedSpread,
	useSelectedZone,
} from "../../hooks/useSelection";
import { useSmartFilterLoader } from "../../hooks/useSmartFilterLoader";
import { isPhotoTransformSupported } from "../bottom-photo-menus/photo-transform-panel/menu-info";
import { DragImage, IDragImageRef } from "../drag-image";
import { GuidelineOverlay, IGuidelineOverlayRef } from "../guideline-overlay";
import { useGuidelineOverlay } from "../guideline-overlay/use-guideline-overlay";
import { useImportPhotosDialog } from "../import-photos-button";
import { ImportPhotosPopover } from "../import-photos-popover/import-photos-popover";
import { PanCropOverlay } from "../pan-crop-overlay.ts";
import { PreviewOverlay } from "../preview-overlay";
import { IZoneSelectionBoxRef, ZoneSelectionBox } from "../zone-selection-box";
import { ZoneSelectionBoxHandlers } from "../zone-selection-box/zone-selection-box-handlers";
import { useAdornerImageCreator } from "./hooks/use-adorner-image-creator";
import { labels } from "./labels";
import { SlideshowPreview } from "./slideshow-preview";
import { createSpreadSlideShowItem, ISpreadSlideShowItem } from "./spread-slideshow-item";
import { useSpreadDirtyChecker } from "./use-dirty-checker";
import { usePreviewOverlay } from "./use-preview-overlay";
import { useBindSlideshowGestures } from "./use-slideshow-gestures";
import { useZoneSelectionBox } from "./use-zone-selection-box";
import { useSpreadUnloadingPreloading } from "./useSpreadUnloadingPreloading";
import { jsonComparer, useMessageToState } from "../../hooks/useMessageToState";
import { SpreadOverview } from "../spread-overview";
import { usePhotoSwapping } from "./hooks/use-photo-swapping";
import { useZoneMove } from "./hooks/use-zone-move";
import { usePhotoPanning } from "./hooks/use-photo-panning";
import { getDefaultMenuForZone } from "../bottom-menu/menu-logic";
import { useMapZones } from "../bottom-menus/bottom-root-menu/use-map-zones";
import { ZoneMenu } from "../zone-menu";
import { useStableCallback } from "../../hooks/useStableCallback";
import { MenuIds, useMenus } from "../../hooks/useMenus";
import { useTextZoneDashboard } from "../../hooks/useTextZoneDashboard";
import { useZoneResize } from "./hooks/use-zone-resize";
import { useShowDesktopLayout } from "../../hooks/useShowdesktopLayout";
import { useArrowKeyPreviewNavigation } from "../../hooks/useArrowKeyPreviewNavigation";
import { useShowAiImport } from "../../hooks/use-allow-ai-import";

// ╔═════════════════════════════════════════════╗
// ║       Slideshow-Preview-Container           ║
// ╚════════╤═══════════════════════╤════════════╝
//          ↓ Create slideshow      ↓ Create / update spread items
// ╔════════════════════╗  ╔═══════════════════════════════╗
// ║ Slideshow          ║  ║ ┌───────────────────────┐     ║
// ╚════════════════════╝  ║ │  SpreadSlideShowItem  │ ┐   ║
//                         ║ └───────────────────────┘ │ ┐ ║
//                         ║   └───────────────────────┘ │ ║
//                         ║     └───────────────────────┘ ║
//                         ╚═══════════════════════════════╝
//
// ------------
// INIT SPREADS:
// ------------
// ╔═════════════════════════╗
// ║ Core: Broadcaster       ║
// ╚═══╤═════════════════════╝
//     │ 1) onMessage (new spreads)
//     ↓
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║          Slideshow-Preview-Container                                      ║
// ╚═══╤══════════════════════════════════════════════╤════════════════════════╝
//     ↓         ↑ 4) onViewChanged                   ↓
// ╔═════════════╧════════════════╗      ╔═════════════════════════════════════╗
// ║ Slideshow                    ║      ║ Spread-slideshow                    ║
// ║                              ║      ║                                     ║
// ║ 3) set items                 ║      ║ 2) create items                     ║
// ║                              ║      ║ ┌─────────────────────────────┐     ║    ┌───────────────────────┐        ┌───────────────────────┐
// ╚══════════════════════════════╝      ║ │ 5) updatePreviewAsync       │ ┐ ──║───→│ renderers             │ ┐ ────→│ DOM elements          │ ┐
//                                       ║ └─────────────────────────────┘ │ ┐ ║    └───────────────────────┘ │ ┐    └───────────────────────┘ │ ┐
//                                       ║   └─────────────────────────────┘ │ ║      └───────────────────────┘ │      └───────────────────────┘ │
//                                       ║     └─────────────────────────────┘ ║        └───────────────────────┘        └───────────────────────┘
//                                       ╚═════════════════════════════════════╝
//
// ---------------
// RENDER PREVIEW:
// ---------------
// ╔═════════════════════════╗
// ║ Core: Broadcaster       ║
// ╚═══╤═════════════════════╝
//     │ 1) onMessage (Spread updated)
//     ↓
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║          Slideshow-Preview-Container                                      ║
// ╚═══╤══════════════════════════════════════════════╤════════════════════════╝
//     ↓         ↑ 4) onItemTransformChanged          ↓
// ╔═════════════╧════════════════╗      ╔═════════════════════════════════════╗
// ║ Slideshow                    ║      ║ Spread-slideshow                    ║
// ║                              ║      ║                                     ║
// ║                              ║      ║ ┌─────────────────────────────┐     ║    ┌───────────────────────┐        ┌───────────────────────┐
// ║ 3) setItemSize               ║      ║ │  2) updatePreviewAsync      │ ┐ ──║───→│ renderers             │ ┐ ────→│ DOM elements          │ ┐
// ║                              ║      ║ │  5) applyTransform          │ │   ║    └───────────────────────┘ │ ┐    └───────────────────────┘ │ ┐
// ║                              ║      ║ └─────────────────────────────┘ │ ┐ ║      └───────────────────────┘ │      └───────────────────────┘ │
// ╚══════════════════════════════╝      ║   └─────────────────────────────┘ │ ║        └───────────────────────┘        └───────────────────────┘
//                                       ║     └─────────────────────────────┘ ║
//                                       ╚═════════════════════════════════════╝

/** Get a list of spreads */
function useSpreads() {
	const [spreads, setSpreads] = useState<readonly eXpress.core.Spread[]>([]);

	const editor = useEditor();

	// Listen for messages
	// --------------------
	useMessage(
		useCallback(
			function onSpreadsChangeDetectMessage(message) {
				// Don't check if interactive
				if (!message.isInteractive && eXpress.core.isSpreadCollectionChanged(message)) {
					const { definition } = editor;
					setSpreads(definition ? [...definition.spreads] : []); // clone spread array because it mutates
				}
			},
			[editor],
		),
	);

	return spreads;
}

export interface IAdornerImageSource {
	readonly uri: string;
	readonly size: eXpress.math.Vector;
}

export const SlideshowPreviewContainer = memo(() => {
	const zoneSelectionBoxRef = useRef<IZoneSelectionBoxRef>(null);

	const showDesktopLayout = useShowDesktopLayout();
	const {
		editor,
		logger,
		defaultFontFamily,
		fontFamilyLoader,
		localizedContent,
		client,
		platform,
	} = useAppContext();

	const dashboard = useDashboardContext();
	const textDashboard = useTextZoneDashboard();

	const importer = useImporter();

	const previousClickLocationRef = useRef<{ x: number; y: number } | null>(null);

	const {
		menuId,
		actions: { setMenu },
	} = useMenus();

	const spreads = useSpreads();

	// Fetch labels for preview
	const [[loadingText]] = useLabels([labels.loadingLabel]);

	const debugPreview = useFeatureFlagToggle("debugPreview");
	const previewContext = usePreviewContext();
	const { previewBox } = usePreviewBox();
	const selectPhotosDialogAsync = useImportPhotosDialog();

	const { slideShowPreviewRef, previewOverlayRef, getZonesAtPoint, mutable, resetZoom } =
		previewContext;

	const spreadDirtyChecker = useSpreadDirtyChecker(() => {
		// Remark:
		// - Called synchronous, be careful to use react state
		// - Should we only rerender interactive messages sync and non-interactive still async (with react state)?

		// At the moment, we only rerender the one spread in view
		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return;

		// Used the selected spread from the slideshow, not from react state (sync callback)
		const selectedSpreadItem = slideShow.items[slideShow.index];

		// Because this message handler is sync before a react update triggers an update of slideShowItems,
		// the slideShowItem could be temporary outdated
		if (!selectedSpreadItem?.spread?.hasOwner()) return;

		updatePreview(selectedSpreadItem);
	});

	const showAiImport = useShowAiImport();
	const [zoneImportAnchor, setZoneImportAnchor] = useState<{ x: number; y: number } | null>(null);
	const zoneImportCallbackRef = useRef<(() => unknown) | null>(null);

	const [photoPreprocessProgress] = usePhotoPreprocessProgress();
	const isPreprocessing = !!photoPreprocessProgress;
	const selectedZone = useSelectedZone();
	const selectedPage = useLogicalSelectedPage();
	const selectedSpread = useSelectedSpread();
	const [isPreviewMode] = useIsPreviewMode();
	useArrowKeyPreviewNavigation();
	const isTextEditMode = useIsTextEditMode();
	const [isTransformMode, setIsTransformMode] = useIsTransformMode();

	const [isPhotoSwapping] = usePhotoSwapping(undefined);
	const [isZoneMoving] = useZoneMove();
	const [isZoneResizing] = useZoneResize();
	const [isPhotoPanning] = usePhotoPanning({});

	/**
	 * Function to update the spread rendering
	 * - Error handling included
	 * - When spread is not dirty it will not re-render
	 */
	const updatePreview = useCallback(
		function updatePreview(item: ISpreadSlideShowItem) {
			if (!item) return;
			const { spread } = item;

			// Don't start rendering of dangling spreads, they be skipped
			if (!spread.hasOwner()) {
				logger.warn(
					`updatePreview() requested for a dangling spread: ${spread?.toString()}`,
				);
				return;
			}

			// If spread is not changed since last render, don't rerender
			if (!spreadDirtyChecker.isDirty(spread)) return;
			spreadDirtyChecker.clearDirty(spread);

			// Get the current zone directly from editor instead of using React state (selectedZone)
			// to avoid stale closure issues when this is called during a state transition
			const currentZone = editor.selection.getCurrentZone() || undefined;

			// Load item
			item.updatePreviewAsync(isPreviewMode, isTransformMode ? currentZone : undefined)
				.then(() => {
					// Show overlay divs from last requested render
					previewOverlayRef.current?.hideSpread(spread, item.isLoading);
				})
				.catch((e: unknown) => {
					client.logErrorAsync(e, false);

					// Hide overlay divs on error
					previewOverlayRef.current?.hideSpread(spread, true);
				});
		},
		[
			client,
			spreadDirtyChecker,
			logger,
			previewOverlayRef,
			isPreviewMode,
			isTransformMode,
			editor,
		],
	);

	/** Item is in view */
	const showPreview = useStableCallback(
		function showPreview(item: ISpreadSlideShowItem) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { spread } = item;

			// Don't start rendering of dangling spreads, they be skipped
			if (!spread.hasOwner()) {
				logger.warn(`showPreview() requested for a dangling spread: ${spread.toString()}`);
				return;
			}

			// If spread is not changed since last render, don't rerender
			if (!spreadDirtyChecker.isDirty(spread)) return;
			spreadDirtyChecker.clearDirty(spread);

			item.showPreviewAsync(isPreviewMode, isTransformMode ? selectedZone : undefined)
				.then(() => {
					// Show overlay divs from last requested render
					previewOverlayRef.current?.hideSpread(spread, item.isLoading);
				})
				.catch((e: unknown) => {
					client.logErrorAsync(e, false);

					// Hide overlay divs on error
					previewOverlayRef.current?.hideSpread(spread, true);
				});
		},
		// HACK: Excluded selectedZone, otherwise the preview became incorrectly zoomed, needs investigation...
	);

	/** Item is not in view anymore */
	const hidePreview = useStableCallback(function hidePreview(item: ISpreadSlideShowItem) {
		const { spread } = item;

		// Set as dirty, so show/update are executed the next time
		spreadDirtyChecker.setDirty(spread);

		item.hidePreview();
	});

	// Initialize slideShow (should be executed once)
	// ---------------------
	useEffect(
		function initSlideShow() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			const unsubscribeOnTransformChange = slideShow.onTransformChange.subscribe(
				function onTransformChange({ transforms }) {
					slideShow.setTransforms({
						slideshowViewIndexTransform: transforms.slideshowViewIndex.transform,
						slideshowViewRegionTransform: transforms.slideshowViewRegion.transform,
					});
				},
			);

			/** Update Slideshow item onItemTransformChange */
			const unsubscribeOnItemTransformChange = slideShow.onItemTransformChange.subscribe(
				function onItemTransformChange({ item, transforms }) {
					item.applyTransform(transforms);
				},
			);

			/**
			 * Set default viewBox if preview size changes
			 * Adorner loads asynchronious and triggers a update
			 * When adorner has a minimal adorner, it needs to be used by default
			 */
			const unsubscribeOnItemSizeChange = slideShow.onItemSizeChange.subscribe(
				function onItemSizeChange({ item, index }) {
					// Update the viewbox
					// The viewbox is for the selected item,
					// so only reset if the size of the current index is changed, not for prerendered items
					if (index === slideShow.index) {
						resetZoom(item.spread);
					}
				},
			);

			// Create object that manages the DOM elements
			return () => {
				unsubscribeOnItemSizeChange();
				unsubscribeOnItemTransformChange();
				unsubscribeOnTransformChange();
			};
		},
		[slideShowPreviewRef, resetZoom],
	);

	// Debug flag
	useEffect(() => {
		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return;

		// slideShow.debug = debugPreview;
		// eslint-disable-next-line react-compiler/react-compiler
		slideShow.showDebugBoxes = debugPreview;
	}, [debugPreview, slideShowPreviewRef]);

	// Preload spreads not in view
	// ----------------------------
	useSpreadUnloadingPreloading(slideShowPreviewRef, showPreview, hidePreview);

	// Shared data between rerenders (for caching)
	const createAdornerImageAsync = useAdornerImageCreator();

	const smartFilterLoader = useSmartFilterLoader();

	// Update Spread Previews (each spread object has a preview)
	// -----------------------
	useEffect(
		function updateSpreadPreviews() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const newSlideShowItems = new Map<eXpress.core.Spread, ISpreadSlideShowItem>();

			const showAdorners = (editor.definition?.product.productAdorners?.size ?? 0) > 0;
			// Create/reuse spreads previews
			spreads.forEach((spread) => {
				const reuseItem = mutable.slideShowItems.get(spread);
				if (reuseItem) {
					newSlideShowItems.set(spread, reuseItem);
					return;
				}

				// Get approximation of render size by fitting spread in portal rect (without adorner, margin, ...)
				const portalSize = previewContext.slideShowPreviewRef.current?.slideShow.portalSize;

				const maxSpreadRenderSize = portalSize
					? eXpress.math.vector(spread).fit(eXpress.math.vector(portalSize))
					: undefined;

				// The rendering currently creates square canvases, so we only need the max side
				let maxSpreadRenderSide = Math.max(
					maxSpreadRenderSize?.width ?? 0,
					maxSpreadRenderSize?.height ?? 0,
				);

				// Allow sharp zoom until 3x
				maxSpreadRenderSide *= 3;

				// Make sure canvas size does not exceed platform limits
				const maxCanvasSide = Math.min(
					platform.canvas.getMaxCanvasSide(platform.browserInfo),
					4096, // Add custom maximum canvas limit = 16MP
				);
				maxSpreadRenderSide = Math.min(maxSpreadRenderSide, maxCanvasSide);

				// Compensate for devicePixelRatio that the rendering will do
				const dpr = globalThis.devicePixelRatio || 1;
				maxSpreadRenderSide /= dpr;

				const item = createSpreadSlideShowItem(
					slideShow,
					spread,
					{
						renderingLoadingText: loadingText, // Not yet needed, when uploading partner Photos
						showDebugLayers: debugPreview,
						showAdorners,
						useSvgLayer: true,
						maxRenderSizeInPx: maxSpreadRenderSide,
					},
					{
						editor,
						logger,
						defaultFontFamily,
						fontFamilyLoader,
						localizedContent,
						client,
						platform,
						createAdornerImageAsync,
						smartFilterLoader,
					},
				);

				newSlideShowItems.set(spread, item);
			});

			// Removed items
			slideShow.items.forEach((item) => {
				// Mark removed spreads again as dirty, so they are rerendered the next time
				spreadDirtyChecker.setDirty(item.spread);

				// Remove from DOM
				if (!newSlideShowItems.has(item.spread)) {
					item.dispose(); // dispose will also remove it from the DOM
				}
			});

			// Add the new items to the slideshow
			mutable.slideShowItems = newSlideShowItems;
			slideShow.items = Array.from(newSlideShowItems.values());

			// Layout items
			slideShow.items.forEach((item) => {
				// We pass the DIP size of the spread as initial size.
				// The slideShow works with relative sizes, so this is okay.
				// When an adorner is present, use its DIP size instead
				const adorner = item.spread.getAdorner();
				const dipSize = adorner?.image ?? item.spread; // Remark: should we pass adorner size if we render no adorner?
				slideShow.setItemSize(item, dipSize);
			});
		},
		[
			spreads,
			debugPreview,
			client,
			defaultFontFamily,
			editor,
			fontFamilyLoader,
			platform,
			loadingText,
			localizedContent,
			logger,
			mutable,
			createAdornerImageAsync,
			slideShowPreviewRef,
			spreadDirtyChecker,
			smartFilterLoader,
			previewContext.slideShowPreviewRef,
		],
	);

	// Bring the selected spread into view
	// --------------------------------
	useEffect(
		function bringSelectedSpreadIntoView() {
			if (!selectedSpread) return;
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { index } = selectedSpread;
			const selectedSpreadItem = slideShow.items[index];
			eXpress.utils.assert(
				selectedSpreadItem?.spread === selectedSpread,
				"The slideshow spreads are not in sync with the definition spreads",
			);

			slideShow.index = index;

			resetZoom(selectedSpread);
		},
		[editor, resetZoom, selectedSpread, slideShowPreviewRef],
	);

	// If constellation changes, the current spread can be moved (example: the cover is removed for a constellation)
	// This should change the selected index in the slideshow to the new spread index position
	useMessageToState({
		name: "keep-selected-spread-into-view",
		filterMessage: (message) =>
			message instanceof eXpress.core.SpreadMovedMessage ||
			eXpress.core.isSelectedConstellationChanged(message),
		getState: useCallback(() => {
			if (!selectedSpread) return;
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { index } = selectedSpread;
			if (index !== slideShow.index) {
				slideShow.index = index;
			}
		}, [selectedSpread, slideShowPreviewRef]),
	});

	// Update slideshow item size (Normally when adorner changes)
	useMessageToState({
		name: "update-slideshow-item-size",
		// Product options can change the adorner without updating the spread
		filterMessage: eXpress.core.isProductOptionChanged,
		getState: useCallback(
			(editor2) => {
				const slideShow = slideShowPreviewRef.current?.slideShow;
				if (!slideShow) return [];

				// Another Product Option changes the adorner => set the new adorner size as slideshow item size
				slideShow.items.forEach((item) => {
					// Because this message handler is sync before a react update triggers an update of slideShowItems,
					// the slideShowItem could be temporary outdated
					if (!item.spread.hasOwner()) return;

					// We pass the DIP size of the spread as initial size.
					// The slideShow works with relative sizes, so this is okay.
					const adorner = item.spread.getAdorner();
					const dipSize = adorner?.image ?? item.spread; // Remark: should we pass adorner size if we render no adorner?
					slideShow.setItemSize(item, dipSize);
				});

				// Returned a value so useMessageToState can detect other cases where adorners change
				return editor2.definition?.product.productAdorners;
			},
			[slideShowPreviewRef],
		),
		comparer: jsonComparer,
	});

	// Update preview overlay
	// -----------------------
	usePreviewOverlay(
		slideShowPreviewRef,
		previewOverlayRef,
		selectedZone,
		selectedSpread,
		mutable,
	);

	// Gestures
	const dragImageRef = useRef<IDragImageRef>(null);
	useBindSlideshowGestures(dragImageRef);

	const { openZoneMapAsync } = useMapZones();

	// ZoneSelectionBox logic
	// -----------------------
	// Update the state when the allowed selection box handlers change
	// Example: when moving/resizing/other zone type is selected
	const [zoneResizingHandle, setZoneResizingHandle] = useState(ZoneSelectionBoxHandlers.None);
	const allowedZoneHandlers = useMemo(
		function getZoneSelectionBoxHandlers() {
			// Hide zonehandlers in Mobile layout, to prevent interfering with text edit transaction.
			if (isTextEditMode && !showDesktopLayout) return ZoneSelectionBoxHandlers.None;

			// Resize
			if (zoneResizingHandle) return zoneResizingHandle;

			// Hide during Transform mode
			if (isTransformMode) return ZoneSelectionBoxHandlers.None;

			// Resize allowed?
			if (dashboard.canResizeZone(selectedZone)) {
				// Customize handlers (Example: symbols only allow uniform scale; photo zone with frames)
				if (
					eXpress.core.isPhotoZone(selectedZone) &&
					(!!selectedZone.effect || selectedZone.isZoneForPhotoSourceType("qr"))
				)
					return ZoneSelectionBoxHandlers.Corners;

				// Symbol zones only allow uniform scaling
				if (eXpress.core.isSymbolZone(selectedZone)) {
					return ZoneSelectionBoxHandlers.Corners;
				}

				// All (depending on available space)
				return ZoneSelectionBoxHandlers.All;
			}

			return ZoneSelectionBoxHandlers.None;
		},
		[
			isTextEditMode,
			zoneResizingHandle,
			isTransformMode,
			dashboard,
			selectedZone,
			showDesktopLayout,
		],
	);

	const isZoneRotateAllowed = useMemo(
		function getZoneRotateAllowed() {
			if (isTextEditMode && !showDesktopLayout) return false;

			// Hide during Transform mode
			if (isTransformMode) return false;

			// Resize allowed?
			if (dashboard.canRotateZone(selectedZone)) {
				return true;
			}

			return false;
		},
		[isTextEditMode, isTransformMode, dashboard, selectedZone, showDesktopLayout],
	);

	useZoneSelectionBox({
		slideShowPreviewRef,
		zoneSelectionBox: zoneSelectionBoxRef.current,
		previewContext,
		allowedZoneHandlers,
		onResize: setZoneResizingHandle,
	});

	// show guidelines
	const guidelineOverlayRef = useRef<IGuidelineOverlayRef>(null);
	const [, shouldUseLines] = useGuidelineOverlay();
	if (guidelineOverlayRef.current) {
		shouldUseLines.start(
			guidelineOverlayRef.current?.el,
			guidelineOverlayRef.current?.drawGuidelines,
			guidelineOverlayRef.current?.removeGuidelines,
			zoneResizingHandle,
		);
	}
	// Rerender selected spread
	// -------------------------
	useEffect(
		function rerenderDirtySpreads() {
			if (!selectedSpread) return;
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { index } = selectedSpread;
			const selectedSpreadItem = slideShow.items[index];

			// The slideshow spreads are not in sync with the definition spreads (see isSpreadCollectionChanged?)
			if (selectedSpreadItem?.spread !== selectedSpread) return;

			// At the moment, we only rerender the one spread in view
			// When we select a spread, we will rerender at here.
			updatePreview(selectedSpreadItem);
		},
		[selectedSpread, slideShowPreviewRef, updatePreview, isTransformMode],
	);

	// Update render if new spread comes into view
	// --------------------------------------------
	useEffect(
		function loadPreviewSlideShowChange() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			/** Get notified if another item is visible */
			return slideShow.onViewChange.subscribe(function onViewChangeShowSpread({ index }) {
				const item = slideShow.items[index];
				if (!item) return;

				// Make sure item that comes into view is not dirty
				showPreview(item);
			});
		},
		[client, slideShowPreviewRef, showPreview],
	);

	// Update slideshow when available preview space changes
	// ------------------------------------------------------
	useEffect(
		function updateWhenPreviewAreaChanged() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			slideShow.updateSlideShow(() => {
				slideShow.portalSize = eXpress.math.vector(
					slideShow.el.clientWidth,
					slideShow.el.clientHeight,
				); // didn't listen to resize/orientationchange events, because I don't think it is needed yet
				if (!slideShow.portalSize.width) debugger;
				if (!previewBox.width) return; // Still default size of 0
				const portalPaddings = eXpress.math.margins(
					previewBox.left,
					previewBox.top,
					Math.ceil(slideShow.portalSize.width - previewBox.left - previewBox.width),
					Math.ceil(slideShow.portalSize.height - previewBox.top - previewBox.height),
				);
				slideShow.portalPaddings = portalPaddings;
			});
		},
		[editor, slideShowPreviewRef, previewBox],
	);

	const { createErrorHandler } = useErrorHandling();

	const { busyBlocker } = useBusyBlockerContext();

	/**
	 * When OSK is open on iOS, the page is partly scrolled outside the viewport
	 * We disabled the native browser gestures in CRE-1634 but
	 * would now like the allow the user to scroll up and down when in text edit mode
	 * HACK: We query the .app element instead of using a ref to app element
	 */
	useEffect(() => {
		if (!platform.browserInfo.isIos()) return; // Not really needed, on Android no panning possible
		const appEl = document.querySelector<HTMLDivElement>(".app");
		if (!appEl) return;
		const isEditingText = menuId === MenuIds.TextValue;
		appEl.style.touchAction = isEditingText ? "pan-y" : "";
	}, [platform, menuId]);

	/** Select a new zone and update the bottom menu for it */
	const selectZone = useCallback(
		function selectZone(newZone: eXpress.core.IZone | undefined) {
			const currentZone = editor.selection.getCurrentZone();
			const currentPage = editor.selection.getLogicalSelectedPage();

			// Enable/disable bottom menu

			const newMenuId = getDefaultMenuForZone(
				menuId,
				currentZone,
				currentPage,
				{
					main: dashboard,
					text: textDashboard,
				},
				isTransformMode,
				newZone || null, // null means no zone
				showDesktopLayout,
			);

			// update menu
			setMenu(newMenuId);

			// Select Zone
			editor.selection.setCurrentZoneAndBroadcast(newZone);
		},
		[
			editor.selection,
			menuId,
			dashboard,
			textDashboard,
			isTransformMode,
			setMenu,
			showDesktopLayout,
		],
	);

	/** Select a new zone and update the bottom menu for it */
	const addToZone = useCallback(
		function addPhotoToZone(
			clickedZone: eXpress.core.IZone | undefined,
			clickPos?: { x: number; y: number },
		) {
			const photoZone = eXpress.core.isPhotoZone(clickedZone) ? clickedZone : undefined;
			if (photoZone) {
				const isSameZoneSelected = clickedZone === selectedZone;
				if (!isSameZoneSelected) return;
				if (isPreprocessing) return;
				if (!dashboard.canApplyPhotoToZone(photoZone)) return;

				const photoSourceType =
					photoZone.photoSourceType ?? photoZone.defaultPhotoSourceType;
				switch (photoSourceType) {
					case "photo": {
						// Import Photo on empty photozone
						if (!photoZone.isEmpty) return;

						const isPhotoSelectionOpen = menuId === MenuIds.PhotoSelection;
						const shouldStartImport = isPhotoSelectionOpen;

						if (shouldStartImport) {
							const runImport = () =>
								busyBlocker
									.during(
										selectPhotosDialogAsync().then((selectedPhotos) => {
											if (selectedPhotos && selectedPhotos.length > 0) {
												// Add first to zone
												if (
													dashboard.canApplyPhotoToZone(
														photoZone,
														selectedPhotos[0],
													)
												) {
													// Async: PhotoZone could already be removed?
													dashboard.applyPhotoToZone(
														photoZone,
														selectedPhotos[0],
													);
												}

												// Add all to photo selection
												editor.addToPhotoSelection(
													selectedPhotos,
													(photos) => {
														importer?.addToQueue(photos);
													},
												);
											}
										}),
									)
									.catch(createErrorHandler());

							if (showAiImport) {
								zoneImportCallbackRef.current = runImport;
								setZoneImportAnchor(clickPos ?? null);
							} else {
								void runImport();
							}
						}
						break;
					}
					case "map": {
						openZoneMapAsync?.().catch(createErrorHandler());
						break;
					}
					case "qr": {
						if (clickedZone?.isEmpty) {
							const warning = new eXpress.GeneralWarning(
								"EmptyQrZoneClickedWarning",
								"slideshow-preview-container",
								"We don't expect empty QR zones",
							);
							createErrorHandler()(warning);
						}

						setMenu(MenuIds.QrEdit);
						break;
					}
					default: {
						eXpress.utils.exhaustiveFail(photoSourceType, true);
						break;
					}
				}
			}
		},
		[
			busyBlocker,
			createErrorHandler,
			dashboard,
			editor,
			importer,
			selectPhotosDialogAsync,
			isPreprocessing,
			selectedZone,
			menuId,
			openZoneMapAsync,
			setMenu,
			showAiImport,
		],
	);

	const onDoubleClick = useCallback(
		function slideShowDoubleClicked({ pageX: x, pageY: y, clientX, clientY }: MouseEvent) {
			// Don't do anything in Preview Mode
			if (isPreviewMode) return;

			const clickedZone = getZonesAtPoint(
				{ x, y },
				(zone) =>
					eXpress.core.isAnyTextZone(zone) ||
					(eXpress.core.isPhotoZone(zone) && dashboard.canApplyPhotoToZone(zone)) ||
					(eXpress.core.isSymbolZone(zone) &&
						(dashboard.editor.isSymbolEditingEnabled() ?? false)),
			).last();

			if (isTransformMode) {
				// Disable transform Mode
				setIsTransformMode(false);
				const newMenuId = getDefaultMenuForZone(
					MenuIds.PhotoTransform,
					selectedZone,
					selectedPage,
					{
						main: dashboard,
						text: textDashboard,
					},
					false,
					clickedZone || null, // null means no zone selected
					showDesktopLayout,
				);
				setMenu(newMenuId); // There is still a photozone selected
			} else if (
				clickedZone &&
				!clickedZone.isEmpty &&
				isPhotoTransformSupported(clickedZone, dashboard)
			) {
				selectZone(clickedZone);

				if (eXpress.core.isPhotoZone(clickedZone) && clickedZone.photoSourceType === "qr") {
					addToZone(clickedZone, { x: clientX, y: clientY });
				} else {
					setIsTransformMode(true);
				}
			} else if (eXpress.core.isAnyTextZone(clickedZone)) {
				selectZone(clickedZone);
				setMenu(MenuIds.Text);
			} else {
				// Add Photo on empty photozone, if needed
				addToZone(clickedZone, { x: clientX, y: clientY });
				selectZone(clickedZone);
			}
		},
		[
			isPreviewMode,
			getZonesAtPoint,
			isTransformMode,
			addToZone,
			setIsTransformMode,
			setMenu,
			selectedZone,
			selectedPage,
			dashboard,
			selectZone,
			textDashboard,
			showDesktopLayout,
		],
	);

	// When product clicked
	// ---------------------
	const onClick = useCallback(
		function slideShowClicked({
			pageX: x,
			pageY: y,
			clientX,
			clientY,
		}: React.MouseEvent<HTMLDivElement>): (() => void) | undefined {
			if (isPreviewMode) return undefined;

			// in desktopLayout we only loop trough zones if the click was on the exact same location
			const clickedSameLocation =
				previousClickLocationRef.current &&
				previousClickLocationRef.current.x === x &&
				previousClickLocationRef.current.y === y;

			let clickedZone: eXpress.core.IZone | undefined = undefined;
			const possibleClickedZones = getZonesAtPoint(
				{ x, y },
				(zone) =>
					(eXpress.core.isAnyTextZone(zone) ||
						(eXpress.core.isPhotoZone(zone) && dashboard.canApplyPhotoToZone(zone)) ||
						(eXpress.core.isSymbolZone(zone) &&
							(dashboard.editor.isSymbolEditingEnabled() ?? false))) &&
					zone.canSelect(),
			).orderBy((z1, z2) => (z1.depth ?? 0) - (z2.depth ?? 0));

			function shouldConfirmSelectedZoneClick(zone: eXpress.core.IZone | undefined): boolean {
				if (!zone || !possibleClickedZones.includes(zone)) return false;

				const cyclesToOverlappingZone =
					((showDesktopLayout && clickedSameLocation) || !showDesktopLayout) &&
					possibleClickedZones.length > 1;

				return (
					!cyclesToOverlappingZone &&
					!zone.isEmpty &&
					isPhotoTransformSupported(zone, dashboard)
				);
			}

			if (shouldConfirmSelectedZoneClick(selectedZone)) {
				const zone = selectedZone;
				if (!zone) return undefined;

				if (isTransformMode) return undefined;

				// Keep hit testing and mode decisions in `onClick`.
				// Only the ambiguous single-click side effect belongs in the returned callback.
				return () => {
					previousClickLocationRef.current = { x, y };
					addToZone(zone, { x: clientX, y: clientY });
				};
			}

			previousClickLocationRef.current = { x, y };

			// click on the same zone
			if (selectedZone && possibleClickedZones.includes(selectedZone)) {
				// if clicked exactly on the same location, cycle trough zones
				if (
					((showDesktopLayout && clickedSameLocation) || !showDesktopLayout) &&
					possibleClickedZones.length > 1
				) {
					// MC mobile: If the current selected zone is empty, we should keep the zone selected and open text-edit/photo add.
					// MC desktop: cycle trough (empty) textzones, but keep empty photozone behaviour to allow to open photo-add on second click
					if (eXpress.core.isPhotoZone(selectedZone) && selectedZone.isEmpty) {
						clickedZone = selectedZone;
					} else if (selectedZone.isEmpty && !showDesktopLayout) {
						clickedZone = selectedZone;
					} else {
						const index = possibleClickedZones.indexOf(selectedZone);
						if (index >= possibleClickedZones.length - 1) {
							clickedZone = possibleClickedZones[0];
						} else {
							clickedZone = possibleClickedZones[index + 1];
						}
					}
				} else {
					// if not exact same location: open text value panel for current zone
					if (eXpress.core.isAnyTextZone(selectedZone)) {
						if (showDesktopLayout) {
							setMenu(MenuIds.Text);
							return undefined;
						}
						clickedZone = selectedZone; // on mobile we want to open text-edit on second click, so we keep the zone selected and open text-edit in the addToZone function
					}
					if (eXpress.core.isPhotoZone(selectedZone) && selectedZone.isEmpty) {
						clickedZone = selectedZone;
					}
				}
			} else {
				// if clicked in another location or no zone selected, select the top zone
				clickedZone = possibleClickedZones.first();
			}

			// When we are in transform mode: clicking another zone exits transform mode and selects the new zone
			if (isTransformMode && clickedZone !== selectedZone) {
				setIsTransformMode(false);

				// Also handle clicking outside any zone: exit transform mode + deselect + leave PhotoTransform menu.
				selectZone(clickedZone);
				const newMenuId = getDefaultMenuForZone(
					MenuIds.PhotoTransform,
					selectedZone,
					selectedPage,
					{
						main: dashboard,
						text: textDashboard,
					},
					false,
					clickedZone || null,
					showDesktopLayout,
				);
				setMenu(newMenuId);
				return undefined;
			}
			if (isTransformMode && clickedZone === selectedZone) {
				// Do nothing
				return undefined;
			}
			if (
				isTextEditMode &&
				clickedZone !== selectedZone &&
				clickedZone instanceof eXpress.core.TextZone
			) {
				addToZone(clickedZone);
				selectZone(clickedZone);
				return undefined;
			}
			// Toggle: clicking the same text zone while text-edit is open closes text-edit mode.
			// also, Leave textEditMode when clicking anywhere outside the selected zone
			if (isTextEditMode) {
				setMenu(MenuIds.Text);
				return undefined;
			}

			// Mobile only: 3rd tap on same text zone → exit edit mode without broadcasting zone change
			// (avoids app.tsx message handler re-entering TextValue)
			if (isTextEditMode && clickedZone === selectedZone && !showDesktopLayout) {
				setMenu(MenuIds.Text);
				return undefined;
			}

			selectZone(clickedZone);
			addToZone(clickedZone, { x: clientX, y: clientY });
			return undefined;
		},
		[
			addToZone,
			getZonesAtPoint,
			selectZone,
			setIsTransformMode,
			setMenu,
			dashboard,
			isPreviewMode,
			isTransformMode,
			selectedZone,
			selectedPage,
			isTextEditMode,
			textDashboard,
			showDesktopLayout,
		],
	);

	const updateMouseCursor = useCallback(
		(x: number, y: number) => {
			// use inner function so we can use early exit to set cursor
			function getCursor() {
				// During actions
				if (isPhotoSwapping) return "grabbing";
				if (isZoneMoving) return "grabbing";
				if (isPhotoPanning) return "grabbing";

				const hoveringZones = getZonesAtPoint(
					{ x, y },
					(zone) =>
						eXpress.core.isAnyTextZone(zone) ||
						(eXpress.core.isPhotoZone(zone) && dashboard.canApplyPhotoToZone(zone)) ||
						(eXpress.core.isSymbolZone(zone) &&
							(dashboard.editor.isSymbolEditingEnabled() ?? false)),
				);

				if (isTransformMode && selectedZone && hoveringZones.includes(selectedZone))
					return "grab"; // During transform mode, we use default cursor

				// Take overlapping zones into account!
				const isHoveringSelectableZone = hoveringZones.some((zone) => zone.canSelect());
				const isHoveringSelectedZone =
					!!selectedZone && hoveringZones.includes(selectedZone);

				/** Not in some Editing/Transform mode*/
				const isDefaultMode = !isTextEditMode && !isTransformMode && !isPreviewMode;

				// Hovering a selectable non-selected zone
				// In Transform mode or Text Edit Mode, don't mark it as clickable, it will exit the mode
				if (!isHoveringSelectedZone && isHoveringSelectableZone && isDefaultMode)
					return "pointer";

				// Hovering an empty selected zone (click to fill)
				if (isHoveringSelectedZone && selectedZone?.isEmpty && isDefaultMode)
					return "pointer";

				// Hovering a movable selected zone
				// In Text Edit mode, resize/move/rotate zone is not possible
				if (isHoveringSelectedZone && selectedZone?.canChangeTransform() && isDefaultMode)
					return "grab";

				return "default";
			}

			// Set cursor
			const style = slideShowPreviewRef.current?.el.style;
			if (!style) return;

			const cursor = getCursor();
			if (cursor) style.cursor = cursor;
		},
		[
			dashboard,
			getZonesAtPoint,
			isPhotoPanning,
			isPhotoSwapping,
			isPreviewMode,
			isTextEditMode,
			isTransformMode,
			isZoneMoving,
			selectedZone,
			slideShowPreviewRef,
		],
	);

	/**
	 * Keep last mouse position, used when cursor needs update without a mouse move \
	 * example:
	 * - Ctrl + Z to undo
	 * - click/select a zone without a mouse move
	 */
	const lastMousePositionRef = useRef({ x: 0, y: 0 });

	const onMouseMove = useCallback(
		function slideShowMouseMove({ pageX: x, pageY: y }: React.MouseEvent) {
			lastMousePositionRef.current = { x, y };
			updateMouseCursor(x, y);
		},
		[updateMouseCursor],
	);

	// Update mouse cursor also on state change, not only on mouse move
	// If one of the dependencies of updateMouseCursor changes, the cursor will be updated
	useEffect(() => {
		const { x, y } = lastMousePositionRef.current || {};
		updateMouseCursor(x, y);
	}, [updateMouseCursor]);

	return (
		<>
			<SlideshowPreview
				ref={slideShowPreviewRef}
				onClick={onClick}
				onMouseMove={onMouseMove}
				onDoubleClick={onDoubleClick}
			/>
			<SpreadOverview />
			<PreviewOverlay ref={previewOverlayRef} />
			<PanCropOverlay />
			<ZoneMenu />
			<GuidelineOverlay ref={guidelineOverlayRef} />
			<ZoneSelectionBox
				ref={zoneSelectionBoxRef}
				handles={allowedZoneHandlers}
				showRotateHandle={isZoneRotateAllowed && !(isZoneMoving || isZoneResizing)}
			/>
			<DragImage ref={dragImageRef} />
			<ImportPhotosPopover
				anchorPoint={zoneImportAnchor}
				open={zoneImportAnchor !== null}
				onOpenChange={(open) => {
					if (!open) setZoneImportAnchor(null);
				}}
				onImportPhotos={() => {
					setZoneImportAnchor(null);
					zoneImportCallbackRef.current?.();
				}}
			/>
		</>
	);
});
SlideshowPreviewContainer.displayName = "SlideshowPreviewContainer";
